/** Strip to digits only — max 12 (Indian Aadhaar). */
export function sanitizeAadhaarDigits(raw: string): string {
  let result = '';
  for (const char of raw) {
    if (char >= '0' && char <= '9') {
      result += char;
      if (result.length >= 12) {
        break;
      }
    }
  }
  return result;
}

export function isMaskedAadhaarDisplay(value: string): boolean {
  return /[X*]/i.test(value);
}

/** Credit-card style grouping: `1234 5678 9012`. */
export function formatAadhaarCardDisplay(digits: string): string {
  const d = sanitizeAadhaarDigits(digits);
  if (!d) {
    return '';
  }
  const parts: string[] = [];
  for (let i = 0; i < d.length; i += 4) {
    parts.push(d.slice(i, i + 4));
  }
  return parts.join(' ');
}

function countDigitsBefore(value: string, cursor: number): number {
  let count = 0;
  const end = Math.min(cursor, value.length);
  for (let i = 0; i < end; i++) {
    if (value[i] >= '0' && value[i] <= '9') {
      count++;
    }
  }
  return count;
}

function cursorFromDigitIndex(display: string, digitIndex: number): number {
  if (digitIndex <= 0) {
    return 0;
  }
  let count = 0;
  for (let i = 0; i < display.length; i++) {
    if (display[i] >= '0' && display[i] <= '9') {
      count++;
      if (count >= digitIndex) {
        return i + 1;
      }
    }
  }
  return display.length;
}

/** Apply grouped display and restore caret while typing. */
export function applyAadhaarInputDisplay(
  input: HTMLInputElement,
  display: string,
  rawBefore: string,
  cursorBefore: number,
): void {
  const digitIndex = countDigitsBefore(rawBefore, cursorBefore);
  input.value = display;
  const nextCursor = cursorFromDigitIndex(display, digitIndex);
  input.setSelectionRange(nextCursor, nextCursor);
}

export function formatAadhaarInputFromRaw(raw: string): {
  digits: string;
  display: string;
} {
  const digits = sanitizeAadhaarDigits(raw);
  return { digits, display: formatAadhaarCardDisplay(digits) };
}

/** Detect Aadhaar fields by binding code or label (workflow packs, KYC forms). */
export function isAadhaarFieldIdentifier(
  fieldCode: string,
  labelOrPlaceholder?: string,
): boolean {
  const code = fieldCode.trim().toLowerCase();
  if (code === 'aadhaarnumber' || code === 'aadhar' || code.includes('aadhaar')) {
    return true;
  }
  const label = (labelOrPlaceholder ?? '').trim();
  return /aadhaar|aadhar/i.test(label);
}
